import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { Loader2, Megaphone, Calendar, Lock } from 'lucide-react'

const PAGE_SIZE = 3

const TYPE_LABEL = {
  event: 'Event',
  group: 'Group',
}

const TYPE_ICON = {
  event: Calendar,
  group: Lock,
}

/**
 * Unified announcements feed for the donor parish page — parish, event, and
 * group announcements merged and sorted by publish date. Shows up to
 * PAGE_SIZE items, with a "View more" button that pages in the rest.
 */
export default function AnnouncementsFeed({ parishId }) {
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    const fetchFirstPage = async () => {
      if (!parishId) return
      try {
        setLoading(true)
        setError(null)
        const response = await api.getParishAnnouncementsFeed(parishId, { page: 1, limit: PAGE_SIZE })
        if (cancelled) return
        setItems(response.data || [])
        setHasNextPage(Boolean(response.meta?.hasNextPage))
        setPage(1)
      } catch (err) {
        console.error('Failed to fetch announcements feed:', err)
        if (!cancelled) setError(err.message || 'Failed to load announcements')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchFirstPage()
    return () => {
      cancelled = true
    }
  }, [parishId])

  const handleViewMore = async () => {
    const nextPage = page + 1
    try {
      setLoadingMore(true)
      const response = await api.getParishAnnouncementsFeed(parishId, { page: nextPage, limit: PAGE_SIZE })
      setItems((prev) => [...prev, ...(response.data || [])])
      setHasNextPage(Boolean(response.meta?.hasNextPage))
      setPage(nextPage)
    } catch (err) {
      console.error('Failed to fetch more announcements:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-gold animate-spin" />
        </div>
      </div>
    )
  }

  if (error || items.length === 0) return null

  return (
    <div className="bg-white border border-surface-border rounded p-6">
      <div className="flex items-center gap-2 mb-4">
        <Megaphone className="w-5 h-5 text-gold" />
        <h3 className="text-xl font-semibold font-display text-charcoal">Announcements</h3>
      </div>
      <div className="space-y-4">
        {items.map((item, index) => {
          const SourceIcon = TYPE_ICON[item.type]
          return (
            <div
              key={`${item.type}-${item.id}`}
              className={`${index < items.length - 1 ? 'pb-4 border-b border-surface-border' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-base font-semibold text-charcoal">{item.title}</h4>
              </div>
              <p className="text-sm text-charcoal/90 leading-relaxed mt-1 whitespace-pre-wrap">
                {item.content}
              </p>
              <div className="flex items-center gap-3 mt-2 text-xs text-charcoal-muted">
                {item.publishedAt && (
                  <span>
                    {new Date(item.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                )}
                {item.authorName && <span>by {item.authorName}</span>}
                {SourceIcon && item.sourceName && (
                  <span className="inline-flex items-center gap-1">
                    <SourceIcon className="w-3 h-3" />
                    {TYPE_LABEL[item.type]}: {item.sourceName}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {hasNextPage && (
        <button
          type="button"
          onClick={handleViewMore}
          disabled={loadingMore}
          className="mt-4 text-sm text-gold hover:text-gold/80 font-medium flex items-center gap-1"
        >
          {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : 'View more'}
        </button>
      )}
    </div>
  )
}
